import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Row, Col, DatePicker } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import startCase from 'lodash/startCase';

import moment from 'moment';
import styles from '../styles';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Breadcrumbs from '../Components/Breadcrumbs';
import { getDailyCompletionList, clearDailyCompletionList } from '../Actions';

const DailyCompletionList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const dailyCompletionList = useSelector(state => state.dailyCompletionList);

  useEffect(() => {
    dispatch(getDailyCompletionList());
    return () => {
      dispatch(clearDailyCompletionList());
    };
  }, []);

  const fabricColumns = useMemo(() => {
    const column = [
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
        sorter: sorter('bpoNo'),
        onFilter: onFilter('bpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Product',
        dataIndex: 'product',
        sorter: sorter('product'),
        onFilter: onFilter('product'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
        sorter: sorter('fabric'),
        onFilter: onFilter('fabric'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Width',
        dataIndex: 'width',
        sorter: sorter('width'),
        onFilter: onFilter('width'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
            sorter: sorter('baseColor'),
            onFilter: onFilter('baseColor'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
            sorter: sorter('printColor'),
            onFilter: onFilter('printColor'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: sorter('quantity'),
        onFilter: onFilter('quantity'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Process',
        dataIndex: 'process',
        sorter: sorter('process'),
        onFilter: onFilter('process'),
        filterDropdown,
        filterIcon,
        render: text => startCase(text),
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Complete',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'dcDate',
            sorter: dateSorter('dcDate'),
            onFilter: onDateFilter('dcDate'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: dcDate =>
              dcDate ? moment(dcDate).format('DD MMM YYYY') : null,
          },
        ],
      },
    ];

    return column;
  }, []);

  const styleColumns = useMemo(() => {
    const column = [
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
        sorter: sorter('bpoNo'),
        onFilter: onFilter('bpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Product',
        dataIndex: 'product',
        sorter: sorter('product'),
        onFilter: onFilter('product'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Style',
        dataIndex: 'name',
        sorter: sorter('name'),
        onFilter: onFilter('name'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        sorter: sorter('description'),
        onFilter: onFilter('description'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Size',
        dataIndex: 'size',
        sorter: sorter('size'),
        onFilter: onFilter('size'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: sorter('quantity'),
        onFilter: onFilter('quantity'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Process',
        dataIndex: 'process',
        sorter: sorter('process'),
        onFilter: onFilter('process'),
        filterDropdown,
        filterIcon,
        render: text => startCase(text),
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Complete',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'dcDate',
            sorter: dateSorter('dcDate'),
            onFilter: onDateFilter('dcDate'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: dcDate =>
              dcDate ? moment(dcDate).format('DD MMM YYYY') : null,
          },
        ],
      },
    ];

    return column;
  }, []);

  const fabricRowKey = useMemo(
    () => record =>
      `ID${record.id}B${record.bpoNo}P${record.productId}F${record.fabricId}Pr${record.process}`,
    []
  );

  const styleRowKey = useMemo(
    () => record =>
      `ID${record.id}B${record.bpoNo}P${record.productId}S${record.styleId}U${record.unitId}D${record.department}Pr${record.process}`,
    []
  );

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1800, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Daily Completion"
        moduleLink="/dailycompletion/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Row>
          <Col className={classes.marginBetweenSelect}>
            <DatePicker
              placeholder="Date"
              onChange={value =>
                dispatch(
                  getDailyCompletionList({
                    date: value ? value.toISOString() : null,
                  })
                )
              }
            />
          </Col>
        </Row>
        <br />
        <Table
          bordered
          size="small"
          columns={fabricColumns}
          dataSource={dailyCompletionList.fabrics}
          rowKey={fabricRowKey}
          pagination={pagination}
          scroll={scroll}
        />
        <br />
        <Table
          bordered
          size="small"
          columns={styleColumns}
          dataSource={dailyCompletionList.styles}
          rowKey={styleRowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

DailyCompletionList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(DailyCompletionList);
